import React from 'react'
import { StructuredText, Image as DatoImage, renderNodeRule, VideoPlayer } from 'react-datocms'
import { isHeading, isLink, isListItem } from 'datocms-structured-text-utils'
import { render as toPlainText } from 'datocms-structured-text-to-plain-text'

import { LocationContext } from '../../components/page-wrapper'
import { NavLink } from '../../storybook/helpers'
import { getSlug } from '../../utils/slugs'

const getBlockAsset = (block) => {
  if (!block || block.__typename !== 'StructuredTextAssetRecord') return null

  switch (block.file.format) {
    case 'png':
    case 'jpg':
    case 'webp':
    case 'jpeg':
      return <DatoImage data={block.file.responsiveImage} className="structure-text-image-block" />
    case 'mp4':
      if (block.file.video) {
        return (
          <VideoPlayer
            data={block.file.video}
            key={block.file.title}
            loop
            playsInline
            autoPlay
            muted
            aria-label="Video Player"
            style={{ width: '100%', '--controls': 'none', pointerEvents: 'none' }}
          />
        )
      }
      return (
        <video loop playsInline autoPlay muted aria-label="Video Player" style={{ width: '100%' }}>
          <source src={block.file.url} type="video/mp4" />
          <track kind="captions" />
        </video>
      )
    default:
      return null
  }
}

const renderHeadingWithAnchor = ({ node, children, key }) => {
  const HeadingTag = `h${node.level}`
  const anchor =
    'section-' +
    toPlainText(node)
      .toLowerCase()
      .replace(/ /g, '-')
      .replace(/[^\w-]+/g, '')

  return (
    <HeadingTag key={key} id={anchor}>
      {children}
    </HeadingTag>
  )
}

function isValidHttpUrl(string) {
  let url

  try {
    url = new URL(string)
  } catch (_) {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}

const renderLink = ({ node, children, key }) => {
  if (!isValidHttpUrl(node.url) && !node.url.startsWith('#')) {
    /* if (node.url) {
      console.error(`Invalid URL: ${node.url}`)
    } */
    return null
  }

  // Is a youtube link
  if (node.url.includes('youtube.com')) {
    return (
      <iframe
        src={`${node.url}`}
        style={{
          display: 'block',
          margin: '0 auto'
        }}
        title="YouTube video player"
        type="text/html"
        width="640"
        height="360"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture ; fullscreen"
        allowFullScreen
        key={key}
      ></iframe>
    )
  }

  return (
    <NavLink key={key} to={node.url}>
      {children}
    </NavLink>
  )
}

const RichText = ({ data, itemProp }) => {
  const location = React.useContext(LocationContext)
  const { slugPrefix } = location

  if (!data.value) return null

  return (
    <div itemProp={itemProp}>
      <StructuredText
        data={data}
        customNodeRules={[
          renderNodeRule(isHeading, ({ node, children, key }) => {
            return renderHeadingWithAnchor({ node, children, key })
          }),
          renderNodeRule(isListItem, ({ key, children }) => {
            return <li key={key}>{children}</li>
          }),
          renderNodeRule(isLink, ({ node, children, key }) => {
            return renderLink({ node, children, key })
          })
        ]}
        renderInlineRecord={({ record }) => {
          switch (record.__typename) {
            case 'BlogPostRecord':
              return <a href={`/blog/${record.slug}`}>{record.title}</a>
            default:
              return null
          }
        }}
        renderLinkToRecord={({ record, children }) => {
          const path = getSlug(record, slugPrefix)
          return <NavLink to={path}>{children}</NavLink>
        }}
        renderBlock={({ record }) => {
          return getBlockAsset(record)
        }}
        renderInlineBlock={({ record }) => {
          switch (record.__typename) {
            case 'MentionBlockRecord':
              return <code>@{record.username}</code>
            default:
              return null
          }
        }}
      />
    </div>
  )
}

export default RichText
